// src/hooks/useContainerWidth.js
import api from "../config/axiosConfig";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  convertToValueLabelFormat,
  formatText,
} from "../utils/utilityFunctions";
import { RFItableColumns, RFItableFilters } from "../utils/constant";
import { debounce } from "lodash";
import theme from "../utils/theme";

export default function useDashboard() {
  const [rfiList, setRFIList] = useState([]);
  const [pieChartData, setPieChartData] = useState(null);
  const [barChartData, setBarChartData] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [sortBy, setSortBy] = useState(RFItableColumns[0].id);
  const [sortDirection, setSortDirection] = useState("desc");
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    perPage: 10,
    page: 1,
    totalPages: 0,
  });

  // State management for filters
  const [open, setOpen] = useState(false);
  const [assignLoader, setAssignLoader] = useState({});
  const [filters, setFilters] = useState({});
  const [newFilterValues, setNewFilterValues] = useState(filters);
  const [applyFiltersTrigger, setApplyFiltersTrigger] = useState(false);
  const [fieldOptions, setFieldOptions] = useState({});
  const [dateValues, setDateValues] = useState({});
  const [loadingStates, setLoadingStates] = useState({
    global: false,
    options: {},
    search: {},
  });
  const [searchTerms, setSearchTerms] = useState(() => {
    const initialSearchTerms = {};
    RFItableFilters.forEach((col) => {
      initialSearchTerms[col.id] = "";
    });
    return initialSearchTerms;
  });
  const returnDesiredDataSet = (data) => {
    return {
      ...data,
      pc_name:
        (data.production_coordinator?.pc_first_name ||
          data.production_coordinator?.pc_last_name) &&
        data.production_coordinator?.pc_first_name +
          " " +
          data.production_coordinator?.pc_last_name,
      qc_name:
        (data.qc_inspector?.qc_first_name || data.qc_inspector?.qc_last_name) &&
        data.qc_inspector?.qc_first_name +
          " " +
          data.qc_inspector?.qc_last_name,
      purpose_type: data.purpose?.purpose_name,
      is_dimensional: data.purpose?.is_dimensional,
      model_name: data.rfi_model?.model_name,
      documents_status: formatText(data.documents_status),
      dimensional_stage: formatText(data.dimensional_stage),
    };
  };

  const handlePageChange = (e, value) => {
    e.stopPropagation();
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDirection("asc");
    }
  };

  const getRFIList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/rfi`, {
        params: {
          page: pagination.page,
          perPage: pagination.perPage,
          ...(applyFiltersTrigger && filters),
          ...(sortBy &&
            sortDirection && {
              sort: [sortBy, sortDirection.toUpperCase()],
            }),
        },
      });
      const desiredDataSet = response?.data?.data?.map(returnDesiredDataSet);
      setRFIList(desiredDataSet || []);
      setPagination((prev) => ({
        ...prev,
        total: response?.data?.count,
        totalPages: response?.data?.totalPages,
        page: response?.data?.currentPage,
        perPage: response?.data?.perPage,
      }));
      return;
    } catch (err) {
      console.error(err);
      toast.error(err.response.data.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.perPage,
    applyFiltersTrigger,
    filters,
    sortBy,
    sortDirection,
  ]);

  const getPiechartData = () => {
    api
      .get("/api/rfi/graph/pie-chart")
      .then((res) => {
        const pieData = [
          {
            id: 0,
            value: res.data.open || 0,
            color: theme.palette.status.open,
            label: "Open",
          },
          {
            id: 1,
            value: res.data.in_progress || 0,
            color: theme.palette.status.inProgress,
            label: "In Progress",
          },
          {
            id: 2,
            value: res.data.closed || 0,
            color: theme.palette.status.closed,
            label: "Closed",
          },
        ];
        setPieChartData({
          pieData,
          total: res.data.total,
        });
      })
      .catch((err) => {
        console.error("getPiechartData error", err);
      });
  };

  const getBarChartData = () => {
    api
      .get("/api/rfi/graph/bar-chart")
      .then((res) => {
        setBarChartData(res.data);
      })
      .catch((err) => {
        console.error("getPiechartData error", err);
      });
  };

  // Filter related functions.

  const fetchFilterOptions = useCallback(
    async (columnId, searchTerm = null) => {
      // if (searchTerm) {
      const res = await api.get("/api/rfi/filter-values/", {
        params: {
          keys: [columnId],
          value: searchTerm,
        },
      });
      if (res.status === 200) {
        const desiredData = convertToValueLabelFormat(res.data.data[columnId]);
        return desiredData;
      }
      // }
      return [];
    },
    []
  );

  const applyFilters = async () => {
    setFilters(newFilterValues);
    setApplyFiltersTrigger(true);
    setPagination({
      total: 0,
      perPage: 10,
      page: 1,
      totalPages: 0,
    });
    // await getRFIList();
  };

  // Normalize options to {label, value} format
  const normalizeOptions = useCallback((options) => {
    return Array.isArray(options)
      ? options.map((opt) =>
          typeof opt === "object" && opt !== null
            ? { label: opt.label || String(opt.value), value: opt.value }
            : { label: String(opt), value: opt }
        )
      : [];
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback(
    (columnId, searchTerm) => {
      const search = async () => {
        try {
          setLoadingStates((prev) => ({
            ...prev,
            search: { ...prev.search, [columnId]: true },
          }));

          const options = await fetchFilterOptions(columnId, searchTerm);
          setFieldOptions((prev) => ({
            ...prev,
            [columnId]: normalizeOptions(options),
          }));
        } catch (error) {
          console.error(`Search failed for ${columnId}:`, error);
        } finally {
          setLoadingStates((prev) => ({
            ...prev,
            search: { ...prev.search, [columnId]: false },
          }));
        }
      };

      if (searchTerm === undefined || searchTerm === "") {
        // Execute immediately without debouncing
        search();
      } else {
        // Use debounced version
        debounce(search, 500)();
      }
    },
    [fetchFilterOptions, normalizeOptions] // Add all dependencies used in the callback
  );

  const loadInitialOptions = useCallback(async () => {
    try {
      setLoadingStates((prev) => ({ ...prev, global: true }));
      const initialOptions = {};
      // Set all options to loading initially
      const initialLoadingStates = RFItableFilters.reduce((acc, column) => {
        acc[column.id] = true;
        return acc;
      }, {});
      setLoadingStates((prev) => ({
        ...prev,
        options: initialLoadingStates,
      }));

      // Create an array of promises for all searchSelect columns
      const promises = RFItableFilters.map(async (column) => {
        try {
          if (column.filterType === "date") {
            initialOptions[column.id] = "";
          } else if (column.filterType === "searchSelect") {
            const options = await fetchFilterOptions(column.id, null);
            initialOptions[column.id] = normalizeOptions(options);
          }
        } catch (error) {
          console.error(`Failed to fetch options for ${column.id}:`, error);
          initialOptions[column.id] = [];
        }
      });

      // Wait for all promises to settle
      await Promise.allSettled(promises);

      // Update state with all results
      setFieldOptions(initialOptions);

      // Set all options to not loading
      const completedLoadingStates = RFItableFilters.reduce((acc, column) => {
        acc[column.id] = false;
        return acc;
      }, {});
      setLoadingStates((prev) => ({
        ...prev,
        options: completedLoadingStates,
      }));
    } catch (error) {
      console.error("Failed to fetch initial options:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, global: false }));
    }
  }, [fetchFilterOptions, normalizeOptions]);

  const handleSearchChange = (columnId, searchTerm) => {
    setSearchTerms((prev) => ({ ...prev, [columnId]: searchTerm }));
    debouncedSearch(columnId, searchTerm);
  };

  // Handle filter selection changes
  const handleFilterChange = (field, selectedOptions, filterType) => {
    if (filterType === "date") {
      setNewFilterValues((prev) => ({ ...prev, [field]: selectedOptions }));
      // setFilters((prev) => ({ ...prev, [field]: selectedOptions }));
    } else {
      const values = selectedOptions.map((option) => option.value);
      setNewFilterValues((prev) => ({ ...prev, [field]: values }));
      // setFilters((prev) => ({ ...prev, [field]: values }));
    }
  };

  // Get currently selected options for a field
  const getSelectedOptions = (field, filterType) => {
    // const currentValues = filters[field] || [];
    const currentValues = newFilterValues[field] || [];
    // const options = fieldOptions[field] || [];
    if (filterType === "date") {
      return currentValues;
    }
    return convertToValueLabelFormat(currentValues);
    // return options.filter((option) => currentValues.includes(option.value));
  };

  const handleApplyFilters = async () => {
    try {
      setLoadingStates((prev) => ({ ...prev, global: true }));
      await applyFilters(newFilterValues);
      setOpen(false);
    } catch (error) {
      console.error("Failed to apply filters:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, global: false }));
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters({});
    setNewFilterValues({});
    setDateValues({});
    setSearchTerms({});
    setApplyFiltersTrigger(true);
  };

  const handleAssignTemplate = (row) => {
    setAssignLoader((prev) => ({ ...prev, [row.rfi_id]: true }));
    api
      .post(`/api/rfi/assign/template`, {
        rfi_id: row.rfi_id,
        model_id: row.model_id,
      })
      .then((res) => {
        getRFIList();
        toast.success(res?.data?.message || "Template assigned successfully!");
      })
      .catch((err) => {
        console.error("err", err);
        toast.error(err?.response?.data?.error || "Something went wrong!");
      })
      .finally(() => {
        setAssignLoader((prev) => ({ ...prev, [row.rfi_id]: false }));
      });
  };

  const handleDateChange = (field, date, filterType) => {
    setDateValues((prev) => ({ ...prev, [field]: date }));
    handleFilterChange(
      field,
      date ? date.toISOString().split("T")[0] : "",
      filterType
    );
  };

  const handleClearDate = (field, e, filterType) => {
    e.stopPropagation();
    setDateValues((prev) => ({ ...prev, [field]: null }));
    handleFilterChange(field, "", filterType);
  };

  // Open/close modal handlers
  const openModal = () => {
    setOpen(true);
    loadInitialOptions();
  };

  const closeModal = () => {
    setOpen(false);
    setApplyFiltersTrigger(true);
  };

  useEffect(() => {
    getRFIList();
    getPiechartData();
    getBarChartData();
  }, [getRFIList, pagination.page, pagination.perPage, filters]);

  return {
    rfiList,
    pagination,
    showAnalytics,
    sortBy,
    sortDirection,
    open,
    filters: newFilterValues,
    fieldOptions,
    searchTerms,
    loadingStates,
    dateValues,
    loading,
    pieChartData,
    barChartData,
    assignLoader,
    handleAssignTemplate,
    handleSearchChange,
    handleFilterChange,
    getSelectedOptions,
    handleApplyFilters,
    handleResetFilters,
    openModal,
    closeModal,
    handleDateChange,
    handleClearDate,
    handlePageChange,
    setShowAnalytics,
    handleSort,
  };
}
